import { useCallback, useEffect, useState } from 'react';
import { LiveTranscriptionMessage } from '@zoom/videosdk';
import { debounce } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { videoTranscripts } from 'Redux/modules/webChat/actions';
import { RootState } from 'Redux/reducer';

import { ZoomClient } from '@/index-types';

function useZoomTranscript(zoomClient: ZoomClient, roomId: number) {
  const {
    data: { enableCallTranscription },
  } = useSelector((state: RootState) => state.webChat.doctor);

  const [transcript, setTranscript] = useState<LiveTranscriptionMessage[]>([]);
  const dispatch = useDispatch();

  const sendVideoTranscript = useCallback(() => {
    dispatch(videoTranscripts(roomId, transcript));
  }, [dispatch, roomId, transcript]);

  useEffect(() => {
    if (!zoomClient || !enableCallTranscription) return;

    const startTranscription = async () => {
      try {
        console.log('Live transcription started');
        await zoomClient.getLiveTranscriptionClient().startLiveTranscription();
      } catch (error) {
        console.error('Error starting live transcription:', error);
      }
    };

    const handleUserAdded = () => {
      startTranscription();
    };

    const handleGetFullTranscripts = async () => {
      try {
        const history = await zoomClient.getLiveTranscriptionClient().getFullTranscriptionHistory();
        if (history.length) setTranscript(history);
      } catch (error) {
        console.error('Error getting full transcription history:', error);
      }
    };

    const handleTranscript = async () => {
      const debouncedGetFullTranscripts = debounce(handleGetFullTranscripts, 300);
      debouncedGetFullTranscripts();
    };

    const handleConnectionChange = async (payload: any) => {
      console.log('🚀 ~ handleConnectionChange ~ payload:', payload);
      if (payload.state === 'Closed') {
        sendVideoTranscript();
      }
    };

    zoomClient.on('user-added', handleUserAdded);
    zoomClient.on('caption-message', handleTranscript);
    zoomClient.on('connection-change', handleConnectionChange);

    return () => {
      zoomClient.off('user-added', handleUserAdded);
      zoomClient.off('caption-message', handleTranscript);
      zoomClient.off('connection-change', handleConnectionChange);
    };
  }, [enableCallTranscription, sendVideoTranscript, zoomClient]);

  return { transcript };
}

export default useZoomTranscript;
